import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, isLoggedIn, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setIsMoreOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setIsUserOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const primaryLinks = [
    { name: 'Home', path: '/', external: false },
    { name: 'Summer Camp Hub', path: 'https://gwinnettcampguide.com', external: true },
    { name: 'TMM Events', path: '/events', external: false },
    { name: 'Things To Do', path: '/things-to-do', external: false },
    { name: 'Local Help', path: '/local-resources', external: false },
    { name: 'What The Duck', path: '/whattheduck', external: false },
  ];

  const moreLinks = [
    { name: 'Mama Approved', path: '/mama-approved', icon: 'ri-medal-fill' },
    { name: 'Partner With Us', path: '/partner', icon: 'ri-handshake-line' },
    { name: 'Membership', path: '/membership', icon: 'ri-vip-crown-line' },
  ];

  const facebookUrl = 'https://www.facebook.com/groups/themommymingle';
  const isMoreActive = moreLinks.some((l) => location.pathname === l.path);

  const handleLogout = async () => {
    await logout();
    setIsUserOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="https://static.readdy.ai/image/0b5cbc7d1d3a0b59b4f12d58a349c104/0e9a9094489a9ea77e178b77c480f264.png"
              alt="The Mommy Mingle"
              className="h-12 w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-0.5">
            {primaryLinks.map((link) =>
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-pink-600' : 'text-white hover:text-pink-200'}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${location.pathname === link.path ? 'text-pink-600' : isScrolled ? 'text-gray-700 hover:text-pink-600' : 'text-white hover:text-pink-200'}`}
                >
                  {link.name}
                </Link>
              )
            )}

            {/* More dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer ${isMoreActive ? 'text-pink-600' : isScrolled ? 'text-gray-700 hover:text-pink-600' : 'text-white hover:text-pink-200'}`}
              >
                More
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMoreOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isMoreOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden py-1">
                  {moreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMoreOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${location.pathname === link.path ? 'bg-pink-50 text-pink-600 font-bold' : 'text-gray-700 hover:bg-gray-50 hover:text-pink-600 font-medium'}`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${link.icon} text-sm`}></i>
                      </div>
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Auth area */}
            {isLoggedIn && user ? (
              <div className="relative ml-3" ref={userRef}>
                <button
                  onClick={() => setIsUserOpen(!isUserOpen)}
                  className="flex items-center gap-2 px-3 py-2 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-full transition-colors cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center bg-pink-500 rounded-full shrink-0">
                    <span className="text-white text-[10px] font-bold">
                      {user.firstName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs font-semibold text-pink-700 whitespace-nowrap">
                    Hi, {user.firstName}
                  </span>
                  <i className={`ri-arrow-down-s-line text-pink-500 text-sm transition-transform ${isUserOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isUserOpen && (
                  <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl border border-gray-100 shadow-lg overflow-hidden py-1">
                    <div className="px-4 py-2.5 border-b border-gray-100">
                      <div className="text-xs font-semibold text-gray-800 truncate">{user.firstName}</div>
                      <div className="text-[10px] text-gray-400 truncate">{user.email}</div>
                    </div>
                    <Link
                      to="/my-rsvps"
                      onClick={() => setIsUserOpen(false)}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600 font-medium cursor-pointer transition-colors"
                    >
                      <i className="ri-ticket-2-line text-sm"></i>
                      My RSVPs
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 font-medium cursor-pointer transition-colors"
                    >
                      <i className="ri-logout-box-r-line text-sm"></i>
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-3 px-5 py-2.5 bg-pink-600 text-white rounded-full text-xs font-bold hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Join The Mommy Mingle
              </a>
            )}
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg cursor-pointer"
          >
            <div className={`w-6 h-6 flex items-center justify-center ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </div>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-1">
            {primaryLinks.map((link) =>
              link.external ? (
                <a
                  key={link.path + link.name}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  {link.name}
                  <i className="ri-external-link-line text-xs text-gray-400 ml-auto"></i>
                </a>
              ) : (
                <Link
                  key={link.path + link.name}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path ? 'bg-pink-50 text-pink-600' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {link.name}
                </Link>
              )
            )}
            {moreLinks.map((link) => (
              <Link
                key={link.path + link.name}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === link.path ? 'bg-pink-50 text-pink-600' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <i className={`${link.icon} text-sm`}></i>
                {link.name}
              </Link>
            ))}
            {isLoggedIn && user ? (
              <>
                <div className="px-4 py-2.5 bg-pink-50 rounded-lg">
                  <div className="text-xs font-semibold text-pink-700">Hi, {user.firstName}</div>
                  <div className="text-[10px] text-gray-400">{user.email}</div>
                </div>
                <Link
                  to="/my-rsvps"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-pink-50 hover:text-pink-600 transition-colors"
                >
                  <i className="ri-ticket-2-line text-sm"></i>
                  My RSVPs
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-rose-600 rounded-lg text-sm font-medium hover:bg-rose-50 cursor-pointer transition-colors"
                >
                  <i className="ri-logout-box-r-line text-sm"></i>
                  Log out
                </button>
              </>
            ) : (
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-2.5 bg-pink-600 text-white rounded-lg text-sm font-bold text-center hover:bg-pink-700 transition-colors cursor-pointer mt-3"
              >
                Join The Mommy Mingle
              </a>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
